import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Separator } from 'src/@/components/ui/separator';
import { Textarea } from 'src/@/components/ui/textarea';

const SETORES = [
    { value: 'financeiro', label: 'Financeiro' },
    { value: 'administrativo', label: 'Administrativo' },
    { value: 'comercial', label: 'Comercial' },
    { value: 'suporte', label: 'Suporte' },
    { value: 'outros', label: 'Outros' },
];

const LABEL_CLS = 'text-xs font-semibold uppercase tracking-wider text-gray-500';
const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';

export default function IdentificacaoSection({ aiAgent, changeModuleConfig }) {
    const identificacao = aiAgent?.config?.module?.realstate?.identificacao || {};
    const telefones = Array.isArray(identificacao.telefonesSetor) ? identificacao.telefonesSetor : [];

    const updateIdentificacao = (patch) => {
        changeModuleConfig({
            identificacao: {
                ...identificacao,
                ...patch,
            },
        });
    };

    const addTelefone = () => {
        updateIdentificacao({ telefonesSetor: [...telefones, { setor: 'financeiro', numero: '' }] });
    };

    const removeTelefone = (index) => {
        updateIdentificacao({ telefonesSetor: telefones.filter((_, i) => i !== index) });
    };

    const updateTelefone = (index, field, value) => {
        updateIdentificacao({
            telefonesSetor: telefones.map((t, i) => (i === index ? { ...t, [field]: value } : t)),
        });
    };

    return (
        <Card className="rounded-xl border border-gray-100 bg-white">
            <CardHeader>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50">
                        <Iconify icon="mdi:office-building-outline" width={20} className="text-violet-600" />
                    </div>
                    <div>
                        <CardTitle>Identificação</CardTitle>
                        <CardDescription>Dados básicos da imobiliária usados pela Lais ao atender clientes.</CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-6">
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>Nome da imobiliária</Label>
                        <Input
                            value={identificacao.nome || ''}
                            disabled
                            title="Para alterar, contate seu especialista"
                            className={`${INPUT_CLS} bg-gray-50 cursor-not-allowed`}
                        />
                        <p className="text-xs text-gray-400">Para alterar, contate seu especialista.</p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>Endereço</Label>
                        <Textarea
                            value={identificacao.endereco || ''}
                            onChange={(e) => updateIdentificacao({ endereco: e.target.value })}
                            placeholder="Av. Paulista, 1000 – Bela Vista, São Paulo/SP – 01310-100"
                            className={INPUT_CLS}
                            rows={2}
                        />
                        <p className="text-xs text-gray-400">Formato: Rua, número – bairro, cidade/UF, CEP.</p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>Site</Label>
                        <Input
                            type="url"
                            value={identificacao.site || ''}
                            onChange={(e) => updateIdentificacao({ site: e.target.value })}
                            placeholder="https://www.exemplo.com.br"
                            className={INPUT_CLS}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>Região de atuação</Label>
                        <Textarea
                            value={identificacao.regiaoAtuacao || ''}
                            onChange={(e) => updateIdentificacao({ regiaoAtuacao: e.target.value })}
                            placeholder="São Paulo, Guarulhos e Osasco"
                            className={INPUT_CLS}
                            rows={2}
                        />
                        <p className="text-xs text-gray-400">Cidades onde você atende.</p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>Horários de funcionamento</Label>
                        <Textarea
                            value={identificacao.horariosFuncionamento || ''}
                            onChange={(e) => updateIdentificacao({ horariosFuncionamento: e.target.value })}
                            placeholder={'Seg–Sex: 9h às 18h\nSáb: 9h às 13h\nDom/Feriados: fechado'}
                            className={INPUT_CLS}
                            rows={3}
                        />
                    </div>
                </div>

                <Separator className="bg-gray-200/60" />

                <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="text-sm font-semibold text-gray-900">Telefones por setor</h4>
                            <p className="text-xs text-gray-500">Números de WhatsApp que a Lais pode informar ao cliente.</p>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addTelefone}
                            className="rounded-xl border-gray-200 hover:border-[#7C3AED] hover:text-[#7C3AED]"
                        >
                            <Iconify icon="eva:plus-outline" width={16} className="mr-1" />
                            Adicionar
                        </Button>
                    </div>

                    {telefones.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                            <Iconify icon="mdi:phone-outline" width={24} className="text-gray-300" />
                            <p className="text-xs text-gray-500 mt-2">Nenhum setor cadastrado</p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {telefones.map((tel, index) => (
                                <div key={index} className="flex items-center gap-2">
                                    <Select
                                        value={tel.setor || 'financeiro'}
                                        onValueChange={(value) => updateTelefone(index, 'setor', value)}
                                    >
                                        <SelectTrigger className={`${INPUT_CLS} w-44`}>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {SETORES.map((s) => (
                                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Input
                                        value={tel.numero || ''}
                                        onChange={(e) => updateTelefone(index, 'numero', e.target.value)}
                                        placeholder="(11) 99999-9999"
                                        className={`${INPUT_CLS} flex-1`}
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeTelefone(index)}
                                        className="rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50"
                                    >
                                        <Iconify icon="eva:trash-2-outline" width={16} />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
